/**
 * #2022 Phase 3 — one captured product row on the unified capture screen.
 *
 * Renders only the fields the active task's project requires:
 *  - Order qty: a [-]/[+] stepper that moves in `order_increment` multiples
 *    (with an "x N" hint when N > 1), shown when the project requires an order.
 *  - On-hand: a free integer count, shown when the project requires inventory.
 *
 * Pure/presentational — all mutations bubble to the parent (useOrderCapture).
 */

import React from 'react';

export interface CaptureRowItem {
  productid: number;
  upc: string;
  description: string;
  cost: number;
  orderqty: number;
  quantityOnHand?: number;
  order_increment?: number;
  isUnknown?: boolean;
  /** #2022 Phase 4 — captured via an OOS shelf-tag scan (records qtyonhand=0). */
  isOOS?: boolean;
}

interface ProductCaptureRowProps {
  item: CaptureRowItem;
  requiresOrder: boolean;
  requiresInventory: boolean;
  /** True when this line's order qty exceeds MaxProductCount (highlight). */
  overMax: boolean;
  onChangeQty: (productid: number, direction: 1 | -1) => void;
  onChangeOnHand: (productid: number, value: number | undefined) => void;
  onRemove: (productid: number) => void;
}

const circleBtn: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: '#334155',
  color: 'white',
  border: 'none',
  fontSize: 18,
  cursor: 'pointer',
};

const ProductCaptureRow: React.FC<ProductCaptureRowProps> = ({
  item,
  requiresOrder,
  requiresInventory,
  overMax,
  onChangeQty,
  onChangeOnHand,
  onRemove,
}) => {
  const increment = item.order_increment && item.order_increment > 1 ? item.order_increment : 1;

  return (
    <div
      style={{
        backgroundColor: item.isOOS ? '#450a0a' : item.isUnknown ? '#78350f' : '#1e293b',
        padding: 12,
        borderRadius: 8,
        border: overMax ? '1px solid #f87171' : '1px solid transparent',
      }}
      data-testid="capture-row"
    >
      {/* Product identity */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.description}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: item.isUnknown ? '#fcd34d' : '#94a3b8' }}>
            {item.upc} | ${item.cost.toFixed(2)} ea
            {item.isUnknown && ' (not in catalog)'}
          </p>
        </div>
        <button
          onClick={() => onRemove(item.productid)}
          aria-label="Remove"
          style={{ ...circleBtn, backgroundColor: '#7f1d1d', color: '#fecaca' }}
        >
          &times;
        </button>
      </div>

      {/* OOS badge — shown IN ADDITION to any order/inventory fields, because a
          rep may deliberately re-order an out-of-stock item. */}
      {item.isOOS && (
        <div style={{ marginTop: 10 }} data-testid="oos-badge">
          <span
            style={{
              display: 'inline-block',
              backgroundColor: '#7f1d1d',
              color: '#fecaca',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.5,
              padding: '4px 10px',
              borderRadius: 999,
            }}
          >
            OUT OF STOCK
          </span>
        </div>
      )}

      {/* Gated capture fields */}
      {(requiresOrder || requiresInventory) && (
      <div style={{ display: 'flex', gap: 20, marginTop: 10, flexWrap: 'wrap' }}>
        {requiresOrder && (
          <div data-testid="order-qty-field">
            <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
              Order qty{increment > 1 ? ` (x${increment})` : ''}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => onChangeQty(item.productid, -1)} style={circleBtn} aria-label="Decrease quantity">
                -
              </button>
              <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }} data-testid="order-qty-value">
                {item.orderqty}
              </span>
              <button onClick={() => onChangeQty(item.productid, 1)} style={circleBtn} aria-label="Increase quantity">
                +
              </button>
            </div>
            {overMax && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#f87171' }}>over max</p>
            )}
          </div>
        )}

        {requiresInventory && (
          <div data-testid="on-hand-field">
            <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>On-hand</label>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={item.quantityOnHand ?? ''}
              placeholder="0"
              onChange={(e) => {
                const raw = e.target.value;
                onChangeOnHand(item.productid, raw === '' ? undefined : Math.max(0, parseInt(raw, 10) || 0));
              }}
              data-testid="on-hand-input"
              style={{
                width: 80,
                padding: '8px 10px',
                backgroundColor: '#0f172a',
                border: '1px solid #334155',
                borderRadius: 8,
                color: 'white',
                fontSize: 16,
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ProductCaptureRow;
